import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Container, Typography, Grid, TextField, Box, MenuItem, IconButton, Snackbar, Modal, SpeedDial, Alert, SpeedDialAction, SpeedDialIcon, Tooltip, LinearProgress, Button, Select, FormControl, Pagination, Checkbox, FormControlLabel, Menu} from '@mui/material';
import { useParams } from 'react-router-dom';
import { useSelector } from 'react-redux';
import axios from 'axios';
import { CircleFlag } from 'react-circle-flags';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import UndoIcon from '@mui/icons-material/Undo';
import { jwtDecode } from "jwt-decode";
import AddCommentIcon from '@mui/icons-material/AddComment';
import TranslateIcon from '@mui/icons-material/Translate';
import AnnotationPanel from './AnnotationPanel';
import NoteIcon from '@mui/icons-material/Note';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import isoLanguages from './isoLanguages';
import LibraryBooksIcon from '@mui/icons-material/LibraryBooks';
import CloseIcon from '@mui/icons-material/Close';
import SpellcheckIcon from '@mui/icons-material/Spellcheck';
import SpellcheckEditor from './SpellcheckEditor';
import TagTextField, { stringToSlate, slateToString } from './TagTextField';
import { languageToolLocales } from './languageToolLocales';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { useNavigate, useLocation } from 'react-router-dom';
import { FileIcon, defaultStyles } from 'react-file-icon';
import FolderIcon from '@mui/icons-material/Folder';
import { clearTranslationDrafts } from '../utils/drafts';
import { buildTagHtml } from '../utils/tagDisplay';


// Get language details from isoLanguages
const getLanguageDetails = (code) => {
    return isoLanguages.find((lang) => lang.code.toLowerCase() === code.toLowerCase()) || { name: code, countryCode: 'UN' };
};

const isLanguageRTL = (code) => {
    const details = getLanguageDetails(code);
    return details.direction === 'rtl';
};


const TranslateProject = () => {
    const { id } = useParams();
    const [originalSections, setOriginalSections] = useState([]);
    const [filteredOriginalSections, setFilteredOriginalSections] = useState([]);
    const [filteredTranslatedSections, setFilteredTranslatedSections] = useState([]);
    const [translatedSections, setTranslatedSections] = useState([]);
    const [approvedSections, setApprovedSections] = useState([]);
    const [sourceLanguage, setSourceLanguage] = useState('');
    const [targetLanguage, setTargetLanguage] = useState('');
    const [overallProgress, setOverallProgress] = useState(0);
    const [wordCount, setWordCount] = useState(0);
    const [charCount, setCharCount] = useState(0);
    const [translationService, setTranslationService] = useState('');
    const [tone, setTone] = useState('default');

    const sourceDirection = isLanguageRTL(sourceLanguage) ? 'rtl' : 'ltr';
    const targetDirection = isLanguageRTL(targetLanguage) ? 'rtl' : 'ltr';
    const [teamMembers, setTeamMembers] = useState([]);
    const [filterNumbers] = useState(false);
    const [filterSymbols] = useState(false);
    const [filterNumbersAndSymbols] = useState(false);
    const [projectCreator, setProjectCreator] = useState('');

    const token = useSelector((state) => state.auth.token);


    const [useTranslationMemory, setUseTranslationMemory] = useState(true);  // State for translation memory toggle
    const [autoTranslateProgress, setAutoTranslateProgress] = useState(0);
    const [isAutoTranslating, setIsAutoTranslating] = useState(false); // Indicates if translation is in progress
    const [glossaries, setGlossaries] = useState([]);
    const [selectedGlossaryId, setSelectedGlossaryId] = useState(null);
    const [selectedGlossaryTerms, setSelectedGlossaryTerms] = useState([]);
    const [filteredGlossaries, setFilteredGlossaries] = useState([]);
    const [saveProgress, setSaveProgress] = useState(0); // Track save progress
    const [isSaving, setIsSaving] = useState(false); // Track save status
    const [setSelectedSectionAnnotations] = useState([]);
    const [isAnnotationPanelOpen, setIsAnnotationPanelOpen] = useState(false);
    const [selectedSectionIndex, setSelectedSectionIndex] = useState(null);
    const [annotatedSections, setAnnotatedSections] = useState(new Set());
    const { id: projectId } = useParams(); // Rename 'id' to 'projectId' for clarity
    const [hoveredIndex, setHoveredIndex] = useState(null);
    const [copied, setCopied] = useState(false);
    const [speedDialDirection, setSpeedDialDirection] = useState('down');
    const rowRef = useRef([]);
    const [preferredAlgorithm, setPreferredAlgorithm] = useState('exact');
    const [skipNumberSegments, setSkipNumberSegments] = useState(false);
    const [skipSymbolSegments, setSkipSymbolSegments] = useState(false);
    const [skipNumberSymbolSegments, setSkipNumberSymbolSegments] = useState(false);
    const [confidenceScores, setConfidenceScores] = useState([]);
    const [openGlossaryDialog, setOpenGlossaryDialog] = useState(false);
    const [filterEmptyTranslations] = useState(false);
    const [translationSources, setTranslationSources] = useState([]); // Track translation sources
    const [newGlossaryTerm, setNewGlossaryTerm] = useState({
        source: '',
        target: '',
        context: '',
        ignore: false,
    });
    const [sectionLinks, setSectionLinks] = useState([]);
    const [tagMaps, setTagMaps] = useState([]);
    const [grammarCheckResults, setGrammarCheckResults] = useState({});
    const [isCheckingGrammar, setIsCheckingGrammar] = useState(false);
    const [grammarAnchorEl, setGrammarAnchorEl] = useState(null);
    const [activeGrammar, setActiveGrammar] = useState({ section: null, match: null });
    const navigate = useNavigate();
    const location = useLocation();
    const [projectName, setProjectName] = useState('');
    const [projectFile, setProjectFile] = useState({ type: 'file', name: '' });
    const [filterMode] = useState('all'); // 'all' | 'untranslated' | 'spelling' | 'comments'
    const [sectionFilter, setSectionFilter] = useState("all");

    const [versionHistory, setVersionHistory] = useState([]);
    const [selectedVersion, setSelectedVersion] = useState('');
    const queryParams = new URLSearchParams(location.search);
    const initialDocVersion = parseInt(queryParams.get('docVersion'), 10);



    const getFileExtension = (name = '') =>
        name.includes('.') ? name.split('.').pop().toLowerCase() : '';

    // inline renderFileIcon (same logic as FileManager)
    const renderFileIcon = ({ type, name }) => {
        if (type === 'folder') {
            return <FolderIcon color="primary" />;
        }
        const ext = getFileExtension(name);
        if (defaultStyles[ext]) {
            return (
                <Box sx={{ width: 32 }}>
                    <FileIcon extension={ext} {...defaultStyles[ext]} />
                </Box>
            );
        }
        return (
            <Box sx={{ width: 32 }}>
                <FileIcon extension={ext || 'file'} />
            </Box>
        );
    };

    // Helper to select the right LanguageTool longCode:
    const getLanguageToolLocale = (src) => {
        if (!src) return 'en-GB';
        const norm = src.toLowerCase();
        // 1) exact match on longCode
        let entry = languageToolLocales.find(l => l.longCode.toLowerCase() === norm);
        if (entry) return entry.longCode;
        // 2) match start (e.g. “en” → first “en-”)
        entry = languageToolLocales.find(l => l.longCode.toLowerCase().startsWith(norm));
        if (entry) return entry.longCode;
        // 3) fallback to matching code only
        entry = languageToolLocales.find(l => l.code.toLowerCase() === norm.split(/[-_]/)[0]);
        return entry ? entry.longCode : 'en-GB';
    };

    let currentUserId;
    if (token) {
        const decodedToken = jwtDecode(token);
        currentUserId = decodedToken.id;
    }

    const user = useSelector((state) => state.auth.user) || {};
    const accountType = (user.accountType || 'essential').toLowerCase();

    const [docVersion, setDocVersion] = useState(
        isNaN(initialDocVersion) ? 0 : initialDocVersion
    );
    const getDraftKey = useCallback(
        (docVer = docVersion) => `translationDraft_${id}_${docVer}`,
        [id, docVersion]
    );
    const [isOffline, setIsOffline] = useState(!navigator.onLine);

    const saveDraft = useCallback(
        (overrideData) => {
            const data = {
                translatedSections,
                approvedSections,
                confidenceScores,
                updatedAt: Date.now(),
                ...overrideData,
            };
            try {
                localStorage.setItem(getDraftKey(), JSON.stringify(data));
            } catch (err) {
                console.error('Failed to save draft', err);
            }
        },
        [getDraftKey, translatedSections, approvedSections, confidenceScores]
    );

    const loadDraft = useCallback(
        (serverUpdatedAt, key) => {
            const draftKey = key || getDraftKey();
            const saved = localStorage.getItem(draftKey);
            if (saved) {
                try {
                    const data = JSON.parse(saved);
                    const draftTime = data.updatedAt || 0;
                    if (!serverUpdatedAt || draftTime > serverUpdatedAt) {
                        if (Array.isArray(data.translatedSections)) {
                            setTranslatedSections(data.translatedSections);
                        }
                        if (Array.isArray(data.approvedSections)) {
                            setApprovedSections(data.approvedSections);
                        }
                        if (Array.isArray(data.confidenceScores)) {
                            setConfidenceScores(data.confidenceScores);
                        }
                        return true;
                    }
                } catch (err) {
                    console.error('Failed to load draft', err);
                }
            }
            return false;
        },
        [getDraftKey]
    );

    const getTagMapForSection = useCallback(
        (sectionIdx) => {
            if (sectionIdx == null) return {};
            const mapsArray = Array.isArray(tagMaps) ? tagMaps : [];
            const linksArray = Array.isArray(sectionLinks) ? sectionLinks : [];
            const masterIdx = linksArray[sectionIdx];

            if (typeof masterIdx === 'number' && mapsArray[masterIdx]) {
                return mapsArray[masterIdx] || {};
            }

            return mapsArray[sectionIdx] || {};
        },
        [tagMaps, sectionLinks]
    );

    useEffect(() => {
        const interval = setInterval(saveDraft, 5000);
        const handleBeforeUnload = () => saveDraft();
        window.addEventListener('beforeunload', handleBeforeUnload);
        return () => {
            clearInterval(interval);
            window.removeEventListener('beforeunload', handleBeforeUnload);
        };
    }, [saveDraft]);

    useEffect(() => {
        const handleOnline = () => setIsOffline(false);
        const handleOffline = () => setIsOffline(true);
        window.addEventListener('online', handleOnline);
        window.addEventListener('offline', handleOffline);
        return () => {
            window.removeEventListener('online', handleOnline);
            window.removeEventListener('offline', handleOffline);
        };
    }, []);

    const [currentPage, setCurrentPage] = useState(1);
    const sectionsPerPage = 50;

    const [currentSections, setCurrentSections] = useState([]);



    // Utility functions for filtering
    const isNumber = (text) => /^[\d\s]+$/.test(text);
    const isSymbol = (text) => /^[^a-zA-Z0-9]+$/.test(text);
    const isNumberOrSymbol = (text) => /^[\d\s\W]+$/.test(text);

    const isProjectOwner = (typeof projectCreator === 'object'
        ? projectCreator._id
        : projectCreator?.toString()) === currentUserId?.toString();

    const isProjectManager = teamMembers.some(
        (m) => m.userId?._id?.toString() === currentUserId?.toString() && m.role === 'lead'
    );

    const canCheckGrammar = (isProjectOwner || isProjectManager) &&
        ['growth', 'scale'].includes(accountType);



    // Fetch user's preferred algorithm when the component mounts
    useEffect(() => {
        const fetchUserSettings = async () => {
            try {
                const response = await axios.get('/api/user-settings', {
                    headers: { Authorization: `Bearer ${token}` },
                });
                setPreferredAlgorithm(response.data.preferredAlgorithm || 'exact'); // Fallback to 'exact'
                setSkipNumberSegments(response.data.preTranslateSettings?.skipNumbers || false);
                setSkipSymbolSegments(response.data.preTranslateSettings?.skipSymbols || false);
                setSkipNumberSymbolSegments(response.data.preTranslateSettings?.skipNumbersAndSymbols || false);
            } catch (error) {
                console.error('Error fetching user settings:', error);
            }
        };

        fetchUserSettings();
    }, [token]);

    useEffect(() => {
        const fetchAnnotations = async () => {
            try {
                const response = await axios.get(`/api/annotations/${projectId}/annotations`, {
                    headers: {
                        Authorization: `Bearer ${token}`, // Include token in the headers
                    },
                });
                const annotations = response.data;

                // Create a set of section indices that have annotations
                const annotatedIndices = new Set(annotations.map(annotation => annotation.sectionIndex));
                setAnnotatedSections(annotatedIndices);
            } catch (error) {
                if (error.response && error.response.status === 401) {
                    console.error('Unauthorized access - please check your credentials.');
                } else {
                    console.error('Error fetching annotations:', error);
                }
            }
        };

        if (projectId) {
            fetchAnnotations();
        }
    }, [projectId, token]);

    // Handler to add a section to annotatedSections
    const handleAnnotationAdded = (sectionIndex) => {
        setAnnotatedSections((prev) => new Set(prev).add(sectionIndex));
    };

    // Inside your TranslateProject component:

    // 1) Updated filter function (no longer resets page)
    const applyFilters = useCallback(() => {
        const filteredOriginal = [];
        const filteredTranslated = [];

        originalSections.forEach((section, index) => {
            const translation = translatedSections[index] || '';
            const hasErrors = (grammarCheckResults[index] || []).length > 0;
            const hasComment = annotatedSections.has(index);

            // first, the existing number/symbol/empty filters
            if (filterNumbers && isNumber(section)) return;
            if (filterSymbols && isSymbol(section)) return;
            if (filterNumbersAndSymbols && isNumberOrSymbol(section)) return;
            if (filterEmptyTranslations && translation.trim() !== '') return;

            // **new** filterMode logic:
            if (filterMode === 'untranslated' && translation.trim() !== '') return;
            if (filterMode === 'spelling' && !hasErrors) return;
            if (filterMode === 'comments' && !hasComment) return;

            filteredOriginal.push(section);
            filteredTranslated.push(translation);
        });

        setFilteredOriginalSections(filteredOriginal);
        setFilteredTranslatedSections(filteredTranslated);
    }, [
        filterNumbers, filterSymbols, filterNumbersAndSymbols, filterEmptyTranslations,
        filterMode,
        originalSections, translatedSections, grammarCheckResults, annotatedSections,
    ]);


    // 2) Reset to page 1 only when filter settings change
    useEffect(() => {
        applyFilters();
        setCurrentPage(1);
    }, [
        filterNumbers,
        filterSymbols,
        filterNumbersAndSymbols,
        filterEmptyTranslations,
        filterMode,      // ← new
    ]);




    const handleAddGlossaryTerm = (index) => {
        // Ensure a glossary is selected
        if (!selectedGlossaryId) {
            alert('Please select a glossary before adding a term.');
            return;
        }

        const originalText = originalSections[index] || '';
        const translatedText = translatedSections[index] || '';

        // Determine if the user is the project owner
        const isProjectOwner = (typeof projectCreator === 'object'
            ? projectCreator._id
            : projectCreator?.toString()) === currentUserId?.toString();

        // Set up the new glossary term but do not submit yet
        const term = {
            source: originalText,
            target: translatedText,
            context: '',
            ignore: false,
            approved: isProjectOwner, // Set approved to false if not the project owner
        };

        // Open dialog with pre-filled term
        setNewGlossaryTerm(term);
        setOpenGlossaryDialog(true);
    };

    const handleCloseGlossaryDialog = () => {
        setOpenGlossaryDialog(false);
        setNewGlossaryTerm({ source: '', target: '', context: '', ignore: false });
    };

    const handleSubmitGlossaryTerm = async () => {
        if (!newGlossaryTerm.source) {
            alert('Source term is required!');
            return;
        }

        try {
            const response = await axios.post(`/api/glossary/${selectedGlossaryId}/terms`, newGlossaryTerm, {
                headers: { Authorization: `Bearer ${token}` },
            });

            if (response.status === 201) {
                alert('Glossary term added successfully!');
                // Optionally update local glossary terms
                setSelectedGlossaryTerms((prevTerms) => [...prevTerms, response.data]);
            }
        } catch (error) {
            console.error('Error adding glossary term:', error);
            alert('Failed to add glossary term.');
        } finally {
            handleCloseGlossaryDialog();
        }
    };


    // Update displayed sections based on currentPage and filtered data
    useEffect(() => {
        const startIndex = (currentPage - 1) * sectionsPerPage;
        const endIndex = startIndex + sectionsPerPage;

        const filtersActive =
            filterEmptyTranslations ||
            filterMode !== 'all' ||
            filterNumbers ||
            filterSymbols ||
            filterNumbersAndSymbols ||
            sectionFilter !== 'all';

        const sections = filtersActive ? filteredOriginalSections : originalSections;
        const translations = filtersActive ? filteredTranslatedSections : translatedSections;

        // Update displayed sections
        setCurrentSections(
            sections.slice(startIndex, endIndex).map((section, index) => ({
                original: section,
                translated: translations[startIndex + index] || '',
            }))
        );
    }, [
        currentPage,
        filteredOriginalSections,
        filteredTranslatedSections,
        originalSections,
        translatedSections,
        sectionsPerPage,
        filterEmptyTranslations,
        filterMode,
        filterNumbers,
        filterSymbols,
        filterNumbersAndSymbols,
        sectionFilter,
    ]);





    // Updated function to highlight and ignore glossary terms based on ignore status
    const highlightGlossaryTerms = (text, terms) => {
        if (typeof text !== 'string') return '';

        const glossaryWords = terms.map(term => ({
            words: term.source.split(/\s+/),
            ignore: term.ignore
        }));
        const words = text.split(/\s+/);
        const highlightedWords = [];
        let i = 0;

        while (i < words.length) {
            let foundMatch = false;

            for (const { words: termWords, ignore } of glossaryWords) {
                if (words[i] === termWords[0]) { // Case-sensitive match
                    let isMatch = true;

                    for (let j = 0; j < termWords.length; j++) {
                        if (words[i + j] !== termWords[j]) {
                            isMatch = false;
                            break;
                        }
                    }

                    if (isMatch) {
                        const termText = termWords.join(' ');

                        highlightedWords.push(
                            `<span title="Glossary Term" style="background-color: #FFFACD; border-radius: 4px; padding: 2px 4px; margin: 0 2px;">${ignore ? `<x>${termText}</x>` : termText
                            }</span>`
                        );

                        i += termWords.length;
                        foundMatch = true;
                        break;
                    }
                }
            }

            if (!foundMatch) {
                highlightedWords.push(words[i]);
                i++;
            }
        }

        return highlightedWords.join(' ');
    };

    const escapeHtml = (unsafe) =>
        unsafe
            .replace(/&/g, '&amp;')
            .replace(/</g, '&lt;')
            .replace(/>/g, '&gt;')
            .replace(/"/g, '&quot;');

    const convertToHtml = (text, map) =>
        text
            .replace(/<\/(\d+)>/g, (_, n) => buildTagHtml(n, false, map))
            .replace(/<(\d+)>/g, (_, n) => buildTagHtml(n, true, map));

    const highlightGrammarIssues = (sectionIdx, text, issues = []) => {
        if (typeof text !== 'string') return '';
        const map = getTagMapForSection(sectionIdx);
        if (!issues.length) return convertToHtml(text, map);

        const sorted = [...issues].sort((a, b) => a.offset - b.offset);
        let result = '';
        let last = 0;

        sorted.forEach(({ offset, length, message }, idx) => {
            result += convertToHtml(text.slice(last, offset), map);
            const problematic = text.substr(offset, length);
            result += `<span class="grammar-error" data-section="${sectionIdx}" data-match="${idx}" title="${escapeHtml(message)}">${convertToHtml(problematic, map)}</span>`;
            last = offset + length;
        });

        result += convertToHtml(text.slice(last), map);
        return result;
    };

    const handleGrammarClick = (e) => {
        const target = e.target;
        if (target.classList.contains('grammar-error')) {
            const section = parseInt(target.getAttribute('data-section'), 10);
            const match = parseInt(target.getAttribute('data-match'), 10);
            setActiveGrammar({ section, match });
            setGrammarAnchorEl(target);
        }
    };

    const applyReplacement = (replacement) => {
        const { section, match } = activeGrammar;
        if (section == null || match == null) return;
        const matches = grammarCheckResults[section] || [];
        const m = matches[match];
        if (!m) return;
        const text = originalSections[section] || '';
        const before = text.slice(0, m.offset);
        const after = text.slice(m.offset + m.length);
        const newText = before + replacement + after;
        const updated = [...originalSections];
        updated[section] = newText;
        setOriginalSections(updated);
        setFilteredOriginalSections(updated);
        setGrammarAnchorEl(null);
    };

    // Apply filters only once on initial render or whenever filter dependencies change
    useEffect(() => {
        applyFilters();
    }, [applyFilters]);

    const calculateCounts = useCallback((sections) => {
        let totalWords = 0;
        let totalChars = 0;
        sections.forEach((section) => {
            const words = section.split(/\s+/).filter(Boolean).length;
            totalWords += words;
            totalChars += section.length;
        });
        setWordCount(totalWords);
        setCharCount(totalChars);
    }, []);

    const calculateOverallProgress = useCallback((approvedSections) => {
        const totalSections = originalSections.length;
        const approvedCount = approvedSections.length;
        const newProgress = totalSections > 0 ? (approvedCount / totalSections) * 100 : 0;
        setOverallProgress(newProgress);
    }, [originalSections.length]);



    useEffect(() => {
        const fetchGlossaries = async () => {
            try {
                const response = await axios.get('/api/glossary', {
                    headers: { Authorization: `Bearer ${token}` },
                });
                setGlossaries(response.data);
                console.log("Fetched glossaries:", response.data);
            } catch (error) {
                console.error("Failed to load glossaries:", error);
            }
        };
        fetchGlossaries();
    }, [token]);

    // Glossary selection and placeholder replacement functions
    const handleGlossarySelection = async (glossaryId, persist = true) => {
        setSelectedGlossaryId(glossaryId);
        if (glossaryId) {
            try {
                const response = await axios.get(`/api/glossary/${glossaryId}/terms`, {
                    headers: { Authorization: `Bearer ${token}` },
                });
                setSelectedGlossaryTerms(response.data);
            } catch (error) {
                console.error("Failed to load glossary terms:", error);
            }
        } else {
            setSelectedGlossaryTerms([]);
        }

        if (persist) {
            try {
                await axios.put(
                    `/api/projects/${id}`,
                    { glossaryId: glossaryId || null },
                    { headers: { Authorization: `Bearer ${token}` } }
                );
            } catch (err) {
                console.error('Error updating project glossary:', err);
            }
        }
    };


    let ignoredTerms = []; // Array to store terms that are ignored

    const createPlaceholders = (text) => {
        let modifiedText = text;
        ignoredTerms = []; // Reset ignored terms for each translation

        selectedGlossaryTerms.forEach((term) => {
            if (term.ignore) {
                const regex = new RegExp(`\\b${term.source}\\b`, 'gi');
                if (regex.test(modifiedText)) {
                    ignoredTerms.push(term.source); // Log the term for later
                    modifiedText = modifiedText.replace(regex, '<x></x>'); // Replace with generic tag
                }
            }
        });

        return modifiedText;
    };

    const replacePlaceholdersWithTerms = (text) => {
        let modifiedText = text;
        let termIndex = 0; // Index to track terms in ignoredTerms array

        modifiedText = modifiedText.replace(/<x><\/x>/g, () => {
            // Replace each placeholder with the corresponding ignored term
            const term = ignoredTerms[termIndex];
            termIndex += 1; // Move to the next term for the next placeholder
            return term;
        });

        return modifiedText;
    };


    // Filter glossaries based on project languages
    useEffect(() => {
        if (glossaries.length > 0 && sourceLanguage && targetLanguage) {
            const matchingGlossaries = glossaries.filter(
                glossary =>
                    glossary.sourceLanguage.toLowerCase() === sourceLanguage.toLowerCase() &&
                    glossary.targetLanguage.toLowerCase() === targetLanguage.toLowerCase()
            );

            setFilteredGlossaries(matchingGlossaries);
        }
    }, [glossaries, sourceLanguage, targetLanguage]);



    useEffect(() => {
        const fetchTeamMembers = async () => {
            try {
                const response = await axios.get('/api/team', {
                    headers: {
                        Authorization: `Bearer ${token}`,
                    },
                });
                setTeamMembers(response.data);
            } catch (err) {
                console.error('Error fetching team members:', err);
            }
        };
        fetchTeamMembers();
    }, [token]);

    useEffect(() => {
        const fetchProject = async () => {
            try {
                const url = !isNaN(initialDocVersion)
                    ? `/api/projects/${id}?docVersion=${initialDocVersion}`
                    : `/api/projects/${id}`;
                const response = await axios.get(url, {
                    headers: { Authorization: `Bearer ${token}` },
                });

                const { project } = response.data;
                setOriginalSections(project.sections || []);
                setTranslatedSections(project.translatedSections || []);
                setConfidenceScores(project.confidenceScores || {});
                setTranslationSources(project.translationSources || new Array((project.sections || []).length).fill(''));
                setFilteredOriginalSections(project.sections || []);
                setFilteredTranslatedSections(project.translatedSections || []);
                setSourceLanguage(project.sourceLanguage);
                setTargetLanguage(project.targetLanguage);
                setProjectCreator(project.createdBy);
                setApprovedSections(project.approvedSections || []);
                setSectionLinks(project.sectionLinks || []); // <-- NEW
                setTagMaps(project.tagMaps || []);
                setTranslationService(project.translationService || 'deepl');
                setTone(project.tone || 'default');
                if (project.glossaryId) {
                    await handleGlossarySelection(project.glossaryId, false);
                }
                calculateCounts(project.sections || []);
                calculateOverallProgress(project.approvedSections || []);
                setProjectName(project.projectName);

                // derive a filename from fileUrl or templateFilePath:
                const path = project.fileUrl || project.templateFilePath || '';
                const filename = path.split('/').pop() || project.projectName;
                setProjectFile({ type: 'file', name: filename });

                const docVer = !isNaN(initialDocVersion)
                    ? initialDocVersion
                    : project.currentVersion || 0;
                setDocVersion(docVer);
                const draftKey = `translationDraft_${id}_${docVer}`;
                const history =
                    (project.documentVersions || [])[docVer]?.translationVersions || [];
                setVersionHistory(history);

                const latestServerUpdatedAt = new Date(
                    history[history.length - 1]?.updatedAt || project.updatedAt || 0
                ).getTime();

                const usedDraft = loadDraft(latestServerUpdatedAt, draftKey);
                if (!usedDraft) {
                    localStorage.removeItem(draftKey);
                }
            } catch (err) {
                console.error('Error fetching project:', err);
                loadDraft();
            }
        };

        fetchProject();
    }, [id, token, initialDocVersion, calculateCounts, calculateOverallProgress, loadDraft]);




    // Fetch annotations for a specific section
    const fetchAnnotations = async (sectionIndex) => {
        try {
            const response = await axios.get(`/api/annotations/${id}/annotations/${sectionIndex}`, {
                headers: { Authorization: `Bearer ${token}` },
            });
            setSelectedSectionAnnotations(response.data);
        } catch (error) {
            console.error('Error fetching annotations:', error);
        }
    };

    // Handle opening annotation panel for a section
    const handleAnnotationOpen = (index) => {
        setSelectedSectionIndex(index);
        setIsAnnotationPanelOpen(true);
        fetchAnnotations(index);
    };

    // Handle closing the annotation panel
    const handleAnnotationClose = () => {
        setIsAnnotationPanelOpen(false);
        setSelectedSectionAnnotations([]);
    };





    const toggleApprovalForSections = (toggleAll) => {
        const sectionsToToggle = toggleAll ? filteredIndices : currentSections;
        const updatedApprovedSections = new Set(approvedSections);

        sectionsToToggle.forEach((section, index) => {
            const globalIndex = toggleAll ? index : index + indexOfFirstSection;

            if (updatedApprovedSections.has(globalIndex)) {
                updatedApprovedSections.delete(globalIndex);  // Unapprove if already approved
            } else {
                updatedApprovedSections.add(globalIndex);  // Approve if not already approved
            }
        });

        setApprovedSections(Array.from(updatedApprovedSections));
        calculateOverallProgress(Array.from(updatedApprovedSections));
    };

    const toggleApproval = async (index) => {
        const globalIndex = index + indexOfFirstSection;
        const isApproved = approvedSections.includes(globalIndex);
        const updatedApproved = isApproved
            ? approvedSections.filter((i) => i !== globalIndex)
            : [...approvedSections, globalIndex];

        setApprovedSections(updatedApproved);
        calculateOverallProgress(updatedApproved);


        try {
            await axios.put(`/api/projects/${id}/toggle-approval`, {
                sectionIndex: globalIndex,
                approved: !isApproved,
            }, {
                headers: {
                    Authorization: `Bearer ${token}`,
                },
            });
        } catch (err) {
            console.error('Error saving toggle state:', err);
        }
    };

    const handleSaveTranslation = async () => {
        setIsSaving(true); // Start showing the progress indicator
        setSaveProgress(0); // Reset progress to 0 at the start

        try {
            // Ensure the arrays have the correct length matching `originalSections`
            const totalSections = originalSections.length;

            // Ensure translatedSections and approvedSections have correct lengths
            const formattedTranslatedSections = Array.from({ length: totalSections }, (_, index) =>
                translatedSections[index] || ''
            );

            const formattedApprovedSections = approvedSections.filter(
                (index) => index < totalSections
            );

            // Ensure confidenceScores is an array of the correct length
            const formattedConfidenceScores = Array.from({ length: totalSections }, (_, index) =>
                confidenceScores[index] || 0
            );

            // Log the payload before sending for debugging
            console.log({
                translatedSections: formattedTranslatedSections,
                approvedSections: formattedApprovedSections,
                confidenceScores: formattedConfidenceScores,
                sections: isProjectOwner ? originalSections : undefined,
            });

            // Send data to the backend
            await axios.put(
                `/api/projects/${id}/translate`,
                {
                    translatedSections: formattedTranslatedSections,
                    approvedSections: formattedApprovedSections,
                    confidenceScores: formattedConfidenceScores,
                    sections: isProjectOwner ? originalSections : undefined,
                },
                {
                    headers: { Authorization: `Bearer ${token}` },
                }
            );
            const { data } = await axios.get(`/api/projects/${id}`, {
                headers: { Authorization: `Bearer ${token}` },
            });
            const proj = data.project;
            const docVer = proj.currentVersion || 0;
            setDocVersion(docVer);
            const history =
                (proj.documentVersions || [])[docVer]?.translationVersions || [];
            setVersionHistory(history);
            setSelectedVersion('');

            // clear saved drafts after successful save
            clearTranslationDrafts();

            alert('Translation saved successfully!');
        } catch (err) {
            console.error('Error saving translation:', err);
            alert('Error saving translation.');
        } finally {
            setIsSaving(false); // Hide the progress indicator when done
            setSaveProgress(0); // Reset progress when saving is complete
        }
    };

    const handleLoadVersion = async (index) => {
        try {
            const params = [];
            if (!isNaN(docVersion)) params.push(`docVersion=${docVersion}`);
            if (index !== '' && index !== null && index !== undefined)
                params.push(`versionIndex=${index}`);
            const url = `/api/projects/${id}` + (params.length ? `?${params.join('&')}` : '');
            const response = await axios.get(url, {
                headers: { Authorization: `Bearer ${token}` },
            });
            const { project } = response.data;
            setOriginalSections(project.sections || []);
            setTranslatedSections(project.translatedSections || []);
            setConfidenceScores(project.confidenceScores || []);
            setTranslationSources(project.translationSources || []);
            setApprovedSections(project.approvedSections || []);
            setSectionLinks(project.sectionLinks || []);
            setTagMaps(project.tagMaps || []);
            calculateCounts(project.sections || []);
            calculateOverallProgress(project.approvedSections || []);
            setProjectName(project.projectName);
            const path = project.fileUrl || project.templateFilePath || '';
            const filename = path.split('/').pop() || project.projectName;
            setProjectFile({ type: 'file', name: filename });

            const history =
                (project.documentVersions || [])[docVersion]?.translationVersions || [];
            setVersionHistory(history);
            clearTranslationDrafts();
        } catch (err) {
            console.error('Error loading version:', err);
            alert('Failed to load version');
        }
    };








    const handleTranslate = async (index) => {
        const originalText = originalSections[index + indexOfFirstSection];

        if (!translationService) {
            alert('Please select a translation service.');
            return;
        }

        // Check if the originalText matches a glossary term with ignore status
        const exactGlossaryTerm = selectedGlossaryTerms.find(
            (term) => term.source === originalText && term.ignore
        );

        if (exactGlossaryTerm) {
            // Use glossary source term with <x> tags if marked as ignored
            const taggedText = `<x>${exactGlossaryTerm.source}</x>`;
            handleTranslationChange(index, taggedText);
            console.log(`Translation source: ignored glossary term | Original Text: "${originalText}" | Tagged Text: "${taggedText}"`);
            return;
        }

        // Proceed with creating placeholders if a glossary is selected
        const textWithPlaceholders = selectedGlossaryId ? createPlaceholders(originalText) : originalText;

        // Ensure source language is set to 'en' if it's 'en-gb'
        const srcLang = sourceLanguage === 'en-gb' ? 'en' : sourceLanguage;

        try {
            let translatedText = '';
            let confidence = 0;
            let source = '';

            if (useTranslationMemory) {
                try {
                    const mem = await axios.post(
                        '/api/translation-memory/check',
                        { userId: currentUserId, originalText, sourceLanguage: srcLang, targetLanguage },
                        {
                            params: { algorithm: preferredAlgorithm },
                            headers: { Authorization: `Bearer ${token}` },
                        }
                    );

                    if (mem.data.translatedText) {
                        translatedText = mem.data.translatedText;
                        confidence = mem.data.confidenceScore || 0;
                        source = 'memory';
                    }
                } catch {
                    // continue to AI
                }
            }

            if (!translatedText) {
                const endpoint = translationService === 'openai'
                    ? '/api/openai/translate'
                    : '/api/translate';
                const response = await axios.post(
                    endpoint,
                    {
                        text: textWithPlaceholders,
                        sourceLanguage: srcLang,
                        targetLanguage,
                        service: translationService,
                        tone,
                    },
                    {
                        headers: { Authorization: `Bearer ${token}` },
                    }
                );

                let apiTranslatedText = response.data.translatedText;
                selectedGlossaryTerms.forEach((term) => {
                    if (term.ignore) {
                        const tagPattern = new RegExp(`<x>${term.source}</x>`, 'gi');
                        apiTranslatedText = apiTranslatedText.replace(tagPattern, term.source);
                    }
                });

                const finalTranslatedText = selectedGlossaryId
                    ? replacePlaceholdersWithTerms(apiTranslatedText)
                    : apiTranslatedText;

                translatedText = finalTranslatedText;
                confidence = 100;
                source = 'ai';
            }

            handleTranslationChange(index, translatedText, confidence, source);

            console.log(`Translation source: ${source === 'memory' ? 'Memory' : 'AI'} | Original Text: "${originalText}" | Translated Text: "${translatedText}"`);

        } catch (err) {
            console.error('Error translating text:', err);
            alert('Error translating text.');
        }
    };










    const handleTranslationChange = async (index, value, confidence = 100, source = null) => {
        const globalIndex = index + indexOfFirstSection;
        const originalText = originalSections[globalIndex];
        const translatedText = value;
        const srcLang = sourceLanguage === 'en-gb' ? 'en' : sourceLanguage;

        // Update the translated sections with the new value
        const updatedTranslations = [...translatedSections];
        updatedTranslations[globalIndex] = translatedText;

        // Apply glossary highlights to the translated text
        const highlightedText = highlightGlossaryTerms(translatedText, selectedGlossaryTerms);
        updatedTranslations[globalIndex] = highlightedText;
        setTranslatedSections(updatedTranslations);

        const updatedSources = [...translationSources];
        if (source) {
            updatedSources[globalIndex] = source;
        }
        setTranslationSources(updatedSources);

        // Update the confidence score for the current section
        setConfidenceScores((prevScores) => ({
            ...prevScores,
            [globalIndex]: confidence,
        }));

        // Save the updated translation and confidence score in the backend
        try {
            await axios.put(`/api/projects/${id}/translate`, {
                translatedSections: updatedTranslations,
                confidenceScores: {
                    ...confidenceScores,
                    [globalIndex]: confidence, // Update the confidence score
                },
                translationSources: updatedSources,
            }, {
                headers: {
                    Authorization: `Bearer ${token}`,
                },
            });
            console.log(`Translation and confidence score saved for section ${globalIndex}`);
        } catch (err) {
            console.error('Error saving translation and confidence score:', err);
        }

    };





    const handleAutoTranslate = async () => {
        if (!translationService) {
            alert('Please select a translation service.');
            return;
        }

        const srcLang = sourceLanguage === 'en-gb' ? 'en' : sourceLanguage;
        const shouldCopySegment = (text) => {
            const trimmed = (text || '').trim();
            if (!trimmed) {
                return false;
            }
            if (skipNumberSegments && isNumber(trimmed)) {
                return true;
            }
            if (skipSymbolSegments && isSymbol(trimmed)) {
                return true;
            }
            if (skipNumberSymbolSegments && isNumberOrSymbol(trimmed)) {
                return true;
            }
            return false;
        };
        const copySegments = originalSections.map(
            (text, idx) =>
                shouldCopySegment(text) &&
                (!translatedSections[idx] || translatedSections[idx].trim() === "")
        );

        // 1) Determine which sections need translation
        // Assume translatedSections is up-to-date and parallel to originalSections
        const needsTranslation = originalSections.map(
            (text, idx) =>
                !copySegments[idx] &&
                (!translatedSections[idx] || translatedSections[idx].trim() === "")
        );

        // 2) Build deduped list and mapping ONLY for untranslated segments
        const uniqueSegments = [];
        const mapping = []; // mapping[rowIndex] → index in uniqueSegments, or -1 if already translated

        originalSections.forEach((text, i) => {
            if (!needsTranslation[i]) {
                mapping.push(-1); // Mark as "already translated"
                return;
            }
            let idx = uniqueSegments.indexOf(text);
            if (idx === -1) {
                uniqueSegments.push(text);
                idx = uniqueSegments.length - 1;
            }
            mapping.push(idx);
        });

        // Edge case: nothing to do!
        if (uniqueSegments.length === 0) {
            if (copySegments.some(Boolean)) {
                const fullTranslatedSections = [...translatedSections];
                const fullConfidenceScores =
                    confidenceScores ? [...confidenceScores] : Array(originalSections.length).fill(0);
                const fullSources =
                    translationSources ? [...translationSources] : Array(originalSections.length).fill('');

                copySegments.forEach((shouldCopy, index) => {
                    if (!shouldCopy) {
                        return;
                    }
                    fullTranslatedSections[index] = originalSections[index] || '';
                    fullConfidenceScores[index] = 100;
                    fullSources[index] = 'copied';
                });

                setTranslatedSections(fullTranslatedSections);
                setConfidenceScores(fullConfidenceScores);
                setTranslationSources(fullSources);

                await axios.put(
                    `/api/projects/${id}/translate`,
                    {
                        translatedSections: fullTranslatedSections,
                        confidenceScores: fullConfidenceScores,
                        translationSources: fullSources,
                    },
                    { headers: { Authorization: `Bearer ${token}` } }
                );
                alert("Pre-translate copy completed.");
                return;
            }
            alert("No untranslated sections found.");
            return;
        }

        // 3) Large project: background job
        if (originalSections.length > 10) {
            alert("Large project: auto-translate will run in the background.");
            try {
                // Send only lightweight metadata; the server will compute the
                // unique segments from the stored project data to avoid a
                // massive payload for large documents.
                await axios.post(
                    `/api/projects/${id}/autotranslate`,
                    {
                        sourceLanguage: srcLang,
                        targetLanguage,
                        needsTranslation,           // Flags for which sections still need translation
                        translationService,
                        useTranslationMemory,
                        preferredAlgorithm,
                        userId: currentUserId,
                        userToken: token,
                        tone,
                    },
                    { headers: { Authorization: `Bearer ${token}` } }
                );

                // Poll for progress as before...
                const pollInterval = setInterval(async () => {
                    try {
                        const { data } = await axios.get(
                            `/api/projects/${id}/autotranslate/status`,
                            { headers: { Authorization: `Bearer ${token}` } }
                        );
                        const { current = 0, total = 1, status } = data;
                        setAutoTranslateProgress(Math.round((current / total) * 100));
                        if (status === 'done') {
                            clearInterval(pollInterval);
                            setIsAutoTranslating(false);
                            setAutoTranslateProgress(100);
                            // Re-fetch final translations
                            const res = await axios.get(`/api/projects/${id}`, {
                                headers: { Authorization: `Bearer ${token}` },
                            });
                            localStorage.removeItem(getDraftKey());
                            const finalTranslated = res.data.project.translatedSections || [];
                            const finalScores = res.data.project.confidenceScores || [];
                            setTranslationSources(res.data.project.translationSources || []);
                            setTranslatedSections(finalTranslated);
                            setConfidenceScores(finalScores);
                            const docVer = res.data.project.currentVersion || 0;
                            setDocVersion(docVer);
                            const history =
                                (res.data.project.documentVersions || [])[docVer]?.translationVersions || [];
                            setVersionHistory(history);
                            setSelectedVersion('');
                            saveDraft({
                                translatedSections: finalTranslated,
                                approvedSections,
                                confidenceScores: finalScores,
                            });
                            alert('Auto-translate completed!');
                        }
                    } catch (e) {
                        clearInterval(pollInterval);
                        setIsAutoTranslating(false);
                        setAutoTranslateProgress(0);
                        alert('Error checking translation progress. Please reload.');
                    }
                }, 2000);
            } catch (err) {
                setIsAutoTranslating(false);
                setAutoTranslateProgress(0);
                alert('Failed to start background auto-translate.');
            }
            return;
        }

        // 4) Small project: inline translate only empty unique segments
        setIsAutoTranslating(true);
        setAutoTranslateProgress(0);
        try {
            const updatedUniqueTranslations = Array(uniqueSegments.length).fill('');
            const updatedUniqueScores = {};
            const updatedUniqueSources = Array(uniqueSegments.length).fill('');

            // Prepare full arrays for real-time progress updates
            const fullTranslatedSections = [...translatedSections];
            const fullConfidenceScores =
                confidenceScores ? [...confidenceScores] : Array(originalSections.length).fill(0);
            const fullSources =
                translationSources ? [...translationSources] : Array(originalSections.length).fill('');

            copySegments.forEach((shouldCopy, index) => {
                if (!shouldCopy) {
                    return;
                }
                fullTranslatedSections[index] = originalSections[index] || '';
                fullConfidenceScores[index] = 100;
                fullSources[index] = 'copied';
            });

            for (let ui = 0; ui < uniqueSegments.length; ui++) {
                const originalText = uniqueSegments[ui];
                let translated = '';
                let confidence = 0;
                let source = '';

                // 4a) Try memory
                if (useTranslationMemory) {
                    try {
                        const mem = await axios.post(
                            '/api/translation-memory/check',
                            { userId: currentUserId, originalText, sourceLanguage: srcLang, targetLanguage },
                            {
                                params: { algorithm: preferredAlgorithm },
                                headers: { Authorization: `Bearer ${token}` },
                            }
                        );
                        if (mem.data.translatedText) {
                            translated = mem.data.translatedText;
                            confidence = mem.data.confidenceScore || 0;
                            source = 'memory';
                        }
                    } catch {
                        /* fallback to AI */
                    }
                }

                // 4b) Fallback to AI service
                if (!translated) {
                    const endpoint = translationService === 'openai' ? '/api/openai/translate' : '/api/translate';
                    const aiRes = await axios.post(
                        endpoint,
                        { text: originalText, sourceLanguage: srcLang, targetLanguage, service: translationService },
                        { headers: { Authorization: `Bearer ${token}` } }
                    );
                    translated = aiRes.data.translatedText;
                    confidence = 100;
                    source = 'ai';
                }

                updatedUniqueTranslations[ui] = translated;
                updatedUniqueScores[ui] = confidence;
                updatedUniqueSources[ui] = source;

                // Update full arrays so progress reflects in real time
                mapping.forEach((uniqueIdx, i) => {
                    if (uniqueIdx === ui) {
                        fullTranslatedSections[i] = translated;
                        fullConfidenceScores[i] = confidence;
                        fullSources[i] = source;
                    }
                });
                setTranslatedSections([...fullTranslatedSections]);
                setConfidenceScores([...fullConfidenceScores]);
                setTranslationSources([...fullSources]);

                setAutoTranslateProgress(Math.round(((ui + 1) / uniqueSegments.length) * 100));
            }

            // Save & update UI
            localStorage.removeItem(getDraftKey());
            await axios.put(
                `/api/projects/${id}/translate`,
                {
                    translatedSections: fullTranslatedSections,
                    confidenceScores: fullConfidenceScores,
                    translationSources: fullSources,
                },
                { headers: { Authorization: `Bearer ${token}` } }
            );
            const res = await axios.get(`/api/projects/${id}`, {
                headers: { Authorization: `Bearer ${token}` },
            });
            const docVer = res.data.project.currentVersion || 0;
            setDocVersion(docVer);
            const history =
                (res.data.project.documentVersions || [])[docVer]?.translationVersions || [];
            setVersionHistory(history);
            setSelectedVersion('');

            setTranslatedSections(fullTranslatedSections);
            setConfidenceScores(fullConfidenceScores);
            setTranslationSources(fullSources);
            saveDraft({
                translatedSections: fullTranslatedSections,
                approvedSections,
                confidenceScores: fullConfidenceScores,
            });
            alert('Auto-translate completed successfully!');
        } catch (error) {
            console.error('Auto-translate error:', error);
            alert('Error during auto-translate. Please try again.');
        } finally {
            setIsAutoTranslating(false);
            setAutoTranslateProgress(0);
        }
    };

    const handleGrammarCheck = async () => {
        setIsCheckingGrammar(true);
        try {
            const locale = getLanguageToolLocale(sourceLanguage);
            const res = await axios.post(
                '/api/grammar/check',
                { language: locale, sections: originalSections },
                { headers: { Authorization: `Bearer ${token}` } }
            );
            const keyed = {};
            (res.data.results || []).forEach(r => {
                const sorted = (r.matches || []).slice().sort((a, b) => a.offset - b.offset);
                keyed[r.index] = sorted;
            });
            setGrammarCheckResults(keyed);
        } catch (err) {
            console.error('Grammar check error:', err);
            setGrammarCheckResults({});
        } finally {
            setIsCheckingGrammar(false);
        }
    };

    const calculateTranslationProgress = useCallback(() => {
        const totalSections = originalSections.length;
        const translatedCount = translatedSections.filter(section => section).length;
        return totalSections > 0 ? (translatedCount / totalSections) * 100 : 0;
    }, [originalSections, translatedSections]);

    const translationProgress = calculateTranslationProgress();

    const handleCopyToClipboard = (text) => {
        navigator.clipboard.writeText(text).then(() => {
            setCopied(true); // Show feedback
        });
    };

    const determineSpeedDialDirection = (index) => {
        const rect = rowRef.current[index]?.getBoundingClientRect();
        if (rect) {
            setSpeedDialDirection(rect.bottom > window.innerHeight - 200 ? 'up' : 'down');
        }
    };

    // Always use the full number of segments from sectionLinks
    // --- Filtered Display Arrays and Pagination ---
    // Build a flat list of all section indices
    const allIndices = originalSections.map((_, i) => i);

    // Filter by dropdown selection
    let filteredIndices = allIndices;
    if (sectionFilter === "untranslated") {
        filteredIndices = filteredIndices.filter(i => !translatedSections[i]?.trim());
    } else if (sectionFilter === "errors") {
        filteredIndices = filteredIndices.filter(i => (grammarCheckResults[i] || []).length > 0);
    } else if (sectionFilter === "comments") {
        filteredIndices = filteredIndices.filter(i => annotatedSections.has(i));
    }

    // Paginate the filtered set
    const totalSections = filteredIndices.length;
    const indexOfLastSection = Math.min(currentPage * sectionsPerPage, totalSections);
    const indexOfFirstSection = (currentPage - 1) * sectionsPerPage;
    const pagedIndices = filteredIndices.slice(indexOfFirstSection, indexOfLastSection);



    return(
        <>
            {isOffline && (
                <Alert severity="warning" sx={{ borderRadius: 0 }}>
                    You're offline. Changes will be saved locally.
                </Alert>
            )}
            {/* Sticky full‑width menu bar */}
            <Box
                sx={{
                    position: 'sticky',
                    top: 0,
                    left: 0,
                    width: '100%',
                    zIndex: 1100,
                    bgcolor: 'background.paper',
                    borderBottom: 1,
                    borderColor: 'divider',
                }}
            >
                <Container
                    maxWidth="lg"
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        py: 1,
                    }}
                >
                    {/* Left side */}
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <IconButton onClick={() => navigate(`/dashboard/projects/${projectId}`)}>
                            <ArrowBackIcon />
                        </IconButton>
                        {renderFileIcon(projectFile)}
                        <Typography variant="h6" noWrap>
                            {projectName}
                        </Typography>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, ml: 2 }}>
                            <CircleFlag
                                countryCode={getLanguageDetails(sourceLanguage).countryCode.toLowerCase()}
                                height="20"
                            />
                            <Typography variant="body1">
                                {getLanguageDetails(sourceLanguage).name}
                            </Typography>
                            <Typography variant="body1" sx={{ mx: 0.5 }}>→</Typography>
                            <Typography variant="body1">
                                {getLanguageDetails(targetLanguage).name}
                            </Typography>
                            <CircleFlag
                                countryCode={getLanguageDetails(targetLanguage).countryCode.toLowerCase()}
                                height="20"
                            />
                        </Box>
                    </Box>

                    {/* Right side: soft/default buttons */}
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        {/* Complete Sections – “text” variant is the softest */}
                        <Button
                            variant="outlined"
                            sx={{ textTransform: 'none', px: 2 }}
                            onClick={() => toggleApprovalForSections(true)}
                        >
                            Complete Sections
                        </Button>

                        {isProjectOwner && (
                            <Button
                                variant="contained"
                                disableElevation
                                sx={{ textTransform: 'none', px: 2 }}
                                onClick={handleAutoTranslate}
                                disabled={isAutoTranslating}
                            >
                                Pre Translate
                            </Button>
                        )}

                        {canCheckGrammar && (
                            <Button
                                variant="outlined"
                                disableElevation
                                sx={{ textTransform: 'none', px: 2 }}
                                startIcon={<SpellcheckIcon />}
                                onClick={handleGrammarCheck}
                                disabled={isCheckingGrammar}
                            >
                                QA Check
                            </Button>
                        )}
                    </Box>

                </Container>
            </Box>

            {/* Main content, flush against the top */}
            <Container maxWidth="lg" sx={{ pt: 2 }}>

                {/* Only show if the user is the project creator */}
                {(typeof projectCreator === 'object' ? projectCreator._id : projectCreator?.toString()) === currentUserId?.toString() && (
                    <>
                        {console.log("User is the project creator, showing additional features")}

                        {/* Inline Selection Row removed as project details now handle translator, glossary and service */}

                        {/* Translation Memory Checkbox */}
                        <Box sx={{ mb: 2 }}>
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        checked={useTranslationMemory}
                                        onChange={(e) => setUseTranslationMemory(e.target.checked)}
                                        color="primary"
                                    />
                                }
                                label="Use Translation Memory"
                            />
                        </Box>

                        {/* Filter + Progress + Counts Row */}
                        <Box
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'space-between',
                                mb: 2,
                                gap: 2,
                            }}
                        >
                            {/* Left: Translation / QA / Counts */}
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 3 }}>
                                {/* Translation Progress */}
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <Typography variant="body2" sx={{ fontWeight: 'medium' }}>Translation:</Typography>
                                    <Box sx={{ width: 100 }}>
                                        <LinearProgress variant="determinate" value={translationProgress} sx={{ height: 8, borderRadius: 1 }} />
                                    </Box>
                                    <Typography variant="body2" sx={{ fontWeight: 'bold' }}>{translationProgress.toFixed(0)}%</Typography>
                                </Box>

                                {/* QA Progress */}
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <Typography variant="body2" sx={{ fontWeight: 'medium' }}>QA:</Typography>
                                    <Box sx={{ width: 100 }}>
                                        <LinearProgress variant="determinate" value={overallProgress} sx={{ height: 8, borderRadius: 1 }} />
                                    </Box>
                                    <Typography variant="body2" sx={{ fontWeight: 'bold' }}>{overallProgress.toFixed(0)}%</Typography>
                                </Box>

                                {/* Word / Character Count */}
                                <Typography variant="body2">
                                    Words: {wordCount} &nbsp;&bull;&nbsp; Chars: {charCount}
                                </Typography>
                            </Box>

                            {/* Right: Show & Version selects */}
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                <Typography variant="body2" sx={{ whiteSpace: 'nowrap' }}>Show:</Typography>
                                <FormControl size="small" variant="outlined" sx={{ minWidth: 200 }}>
                                    <Select
                                        value={sectionFilter}
                                        onChange={e => { setSectionFilter(e.target.value); setCurrentPage(1); }}
                                        sx={{ '& .MuiSelect-select': { py: 0.5, px: 1 } }}
                                    >
                                        <MenuItem value="all">All Sections</MenuItem>
                                        <MenuItem value="untranslated">Only Untranslated</MenuItem>
                                        <MenuItem value="errors">With Spelling Mistakes</MenuItem>
                                        <MenuItem value="comments">With Comments</MenuItem>
                                    </Select>
                                </FormControl>


                                <Typography variant="body2" sx={{ whiteSpace: 'nowrap' }}>Translation Version:</Typography>
                                <FormControl size="small" variant="outlined" sx={{ minWidth: 200 }}>
                                    <Select
                                        displayEmpty
                                        value={selectedVersion}
                                        onChange={e => {
                                            const idx = e.target.value;
                                            setSelectedVersion(idx);
                                            handleLoadVersion(idx);
                                        }}
                                        sx={{ '& .MuiSelect-select': { py: 0.5, px: 1 } }}
                                    >
                                        <MenuItem value="">Current</MenuItem>
                                        {versionHistory.map((v, i) => (
                                            <MenuItem key={i} value={i}>
                                                {i}: {new Date(v.updatedAt).toLocaleString()}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                            </Box>
                        </Box>



                        {/* Auto‑translate progress */}
                        {isAutoTranslating && (
                            <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
                                <Box sx={{ width: '50%', mr: 2 }}>
                                    <LinearProgress variant="determinate" value={autoTranslateProgress} />
                                    <Typography variant="body2" color="textSecondary" align="center">
                                        {autoTranslateProgress.toFixed(2)}% completed
                                    </Typography>
                                </Box>
                            </Box>
                        )}
                    </>
                )}

                {/* Sections grid */}
                <Grid container spacing={4}>
                    {pagedIndices.map((globalIndex, pageIdx) => {
                        const originalText = originalSections[globalIndex];
                        const translatedText = translatedSections[globalIndex] || "";
                        const masterIdx = sectionLinks[globalIndex];
                        const isRepeat = sectionLinks.filter(idx => idx === masterIdx).length > 1;
                        const tagMap = getTagMapForSection(globalIndex);

                        return (
                            <Grid item xs={12} key={globalIndex}>
                                <Box
                                    ref={el => rowRef.current[pageIdx] = el}
                                    onMouseEnter={() => {
                                        setHoveredIndex(globalIndex);
                                        determineSpeedDialDirection(globalIndex);
                                    }}
                                    onMouseLeave={() => setHoveredIndex(null)}
                                    sx={{ position: 'relative', py: 1 }}
                                >
                                    <Grid container spacing={2} alignItems="flex-start">
                                        {/* Segment Number + RP */}
                                        <Grid item sx={{ width: 60, position: 'relative', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                            {isRepeat && (
                                                <Tooltip title="Repeated Section">
                                                    <Box component="span" sx={{
                                                        background: '#e1e7f5',
                                                        color: '#234fd2',
                                                        borderRadius: 1,
                                                        fontSize: '0.75rem',
                                                        fontWeight: 600,
                                                        px: 1, mr: 1,
                                                        border: '1px solid #bfc9e8'
                                                    }}>
                                                        RP
                                                    </Box>
                                                </Tooltip>
                                            )}
                                            <Typography variant="body2">{globalIndex + 1}</Typography>
                                            {annotatedSections.has(globalIndex) && (
                                                <Tooltip title="This section has a note">
                                                    <IconButton size="small" color="secondary" sx={{ position: 'absolute', bottom: 0, right: -20 }}>
                                                        <NoteIcon fontSize="small" />
                                                    </IconButton>
                                                </Tooltip>
                                            )}
                                        </Grid>

                                        {/* Original Text */}
                                        <Grid item xs>
                                            {isProjectOwner ? (
                                                <SpellcheckEditor
                                                    dir={sourceDirection}
                                                    tagMap={tagMap}
                                                    style={{
                                                        minHeight: 56,
                                                        border: '1px solid rgba(0,0,0,0.23)',
                                                        borderRadius: 4,
                                                        padding: '16.5px 14px',
                                                        boxSizing: 'border-box',
                                                        textAlign: sourceDirection === 'rtl' ? 'right' : 'left'
                                                    }}
                                                    value={originalText}
                                                    onChange={newText => {
                                                        const u = [...originalSections];
                                                        u[globalIndex] = newText;
                                                        setOriginalSections(u);
                                                        //setFilteredOriginalSections(u);
                                                    }}
                                                    errors={grammarCheckResults[globalIndex] || []}
                                                    onReplace={(sug, errIdx) => {
                                                        setGrammarCheckResults(prev => {
                                                            const next = { ...prev };
                                                            const list = [...(next[globalIndex] || [])];
                                                            list.splice(errIdx, 1);
                                                            next[globalIndex] = list;
                                                            return next;
                                                        });
                                                    }}
                                                    readOnly={false}
                                                />
                                            ) : (
                                                <Box
                                                    sx={{ border: '1px solid rgba(0,0,0,0.23)', borderRadius: 1, p: '16.5px 14px', minHeight: 56, whiteSpace: 'pre-wrap', textAlign: sourceDirection === 'rtl' ? 'right' : 'left' }}
                                                    dir={sourceDirection}
                                                    dangerouslySetInnerHTML={{ __html: highlightGrammarIssues(globalIndex, originalText, grammarCheckResults[globalIndex] || []) }}
                                                    onClick={handleGrammarClick}
                                                />
                                            )}
                                        </Grid>

                                        {/* Translated Text */}
                                        <Grid item xs>
                                            <TagTextField
                                                value={translatedText}
                                                tagMap={tagMap}
                                                onChange={v => {
                                                    setTranslatedSections(prev => {
                                                        const u = [...prev];
                                                        u[globalIndex] = v;
                                                        return u;
                                                    });
                                                }}
                                                dir={targetDirection}
                                                style={{
                                                    textAlign: targetDirection === 'rtl' ? 'right' : 'left',
                                                    backgroundColor: approvedSections.includes(globalIndex) ? '#f0f0f0' : 'white',
                                                    minHeight: 56,
                                                    border: '1px solid rgba(0,0,0,0.23)',
                                                    borderRadius: 4,
                                                    padding: '16.5px 14px',
                                                    width: '100%',
                                                    boxSizing: 'border-box',
                                                }}
                                                disabled={approvedSections.includes(globalIndex)}
                                            />
                                            <Typography variant="caption" color="textSecondary" sx={{ mt: 0.5, display: 'block' }}>
                                                {translationSources[globalIndex]
                                                    ? `${translationSources[globalIndex] === 'memory' ? 'Memory' : 'AI'} (${Math.round(confidenceScores[globalIndex] || 0)}%)`
                                                    : `Confidence: ${Math.round(confidenceScores[globalIndex] || 0)}%`}
                                            </Typography>
                                        </Grid>

                                        {/* Actions */}
                                        <Grid item sx={{ width: 100, display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                                            {hoveredIndex === globalIndex && (
                                                <SpeedDial
                                                    ariaLabel="Actions"
                                                    icon={<SpeedDialIcon />}
                                                    direction={speedDialDirection}
                                                    sx={{
                                                        position: 'absolute',
                                                        top: speedDialDirection === 'down' ? 0 : 'auto',
                                                        bottom: speedDialDirection === 'up' ? 0 : 'auto',
                                                        right: 0,
                                                        zIndex: 1500
                                                    }}
                                                >
                                                    <SpeedDialAction
                                                        icon={approvedSections.includes(globalIndex) ? <UndoIcon /> : <CheckCircleIcon />}
                                                        tooltipTitle={approvedSections.includes(globalIndex) ? 'Unapprove' : 'Approve'}
                                                        onClick={() => toggleApproval(globalIndex - indexOfFirstSection)}
                                                    />
                                                    {isProjectOwner && (
                                                        <SpeedDialAction
                                                            icon={<TranslateIcon />}
                                                            tooltipTitle="Translate"
                                                            onClick={() => handleTranslate(globalIndex - indexOfFirstSection)}
                                                        />
                                                    )}
                                                    <SpeedDialAction
                                                        icon={<AddCommentIcon />}
                                                        tooltipTitle="Add Annotation"
                                                        onClick={() => handleAnnotationOpen(globalIndex)}
                                                    />
                                                    <SpeedDialAction
                                                        icon={<ContentCopyIcon />}
                                                        tooltipTitle="Copy"
                                                        onClick={() => handleCopyToClipboard(translatedText)}
                                                    />
                                                    <SpeedDialAction
                                                        icon={<LibraryBooksIcon />}
                                                        tooltipTitle="Glossary Term"
                                                        onClick={() => handleAddGlossaryTerm(globalIndex)}
                                                    />
                                                </SpeedDial>
                                            )}
                                        </Grid>
                                    </Grid>
                                </Box>
                            </Grid>
                        );
                    })}
                </Grid>

                {/* Snackbar for Copy Feedback */}
                <Snackbar
                    open={copied}
                    autoHideDuration={2000}
                    onClose={() => setCopied(false)}
                    anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
                >
                    <Alert onClose={() => setCopied(false)} severity="success" sx={{ width: '100%' }}>
                        Text copied to clipboard!
                    </Alert>
                </Snackbar>

                {/* Glossary Term Popup */}
                <Modal open={openGlossaryDialog} onClose={handleCloseGlossaryDialog}>
                    <Box
                        sx={{
                            position: 'absolute',
                            top: '50%',
                            left: '50%',
                            transform: 'translate(-50%, -50%)',
                            width: 400,
                            bgcolor: 'background.paper',
                            boxShadow: 24,
                            p: 3,
                            borderRadius: 2,
                        }}
                    >
                        <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                            <Typography variant="h6">Add Glossary Term</Typography>
                            <IconButton onClick={handleCloseGlossaryDialog}>
                                <CloseIcon />
                            </IconButton>
                        </Box>
                        <TextField
                            autoFocus
                            margin="dense"
                            label="Source Term"
                            type="text"
                            fullWidth
                            value={newGlossaryTerm.source}
                            onChange={(e) => setNewGlossaryTerm({ ...newGlossaryTerm, source: e.target.value })}
                            sx={{ mb: 2 }}
                            inputProps={{ spellCheck: false, dir: sourceDirection, style: { textAlign: sourceDirection === 'rtl' ? 'right' : 'left' } }}
                        />
                        <FormControlLabel
                            control={
                                <Checkbox
                                    checked={newGlossaryTerm.ignore}
                                    onChange={(e) =>
                                        setNewGlossaryTerm({
                                            ...newGlossaryTerm,
                                            ignore: e.target.checked,
                                            target: e.target.checked ? '' : newGlossaryTerm.target,
                                        })
                                    }
                                    color="primary"
                                />
                            }
                            label="Ignore Term"
                            sx={{ mb: 2 }}
                        />
                        <TextField
                            margin="dense"
                            label="Target Term"
                            type="text"
                            fullWidth
                            value={newGlossaryTerm.target}
                            onChange={(e) => setNewGlossaryTerm({ ...newGlossaryTerm, target: e.target.value })}
                            disabled={newGlossaryTerm.ignore}
                            sx={{ mb: 2 }}
                            inputProps={{ spellCheck: false, dir: targetDirection, style: { textAlign: targetDirection === 'rtl' ? 'right' : 'left' } }}
                        />
                        <TextField
                            margin="dense"
                            label="Context"
                            type="text"
                            fullWidth
                            value={newGlossaryTerm.context}
                            onChange={(e) => setNewGlossaryTerm({ ...newGlossaryTerm, context: e.target.value })}
                            placeholder="Optional usage information"
                            sx={{ mb: 2 }}
                            inputProps={{ spellCheck: false }}
                        />
                        <Button
                            variant="contained"
                            fullWidth
                            onClick={handleSubmitGlossaryTerm}
                            disabled={!newGlossaryTerm.source.trim()}
                        >
                            Submit
                        </Button>
                    </Box>
                </Modal>

                {/* Grammar Suggestions Menu */}
                <Menu
                    anchorEl={grammarAnchorEl}
                    open={Boolean(grammarAnchorEl)}
                    onClose={() => setGrammarAnchorEl(null)}
                    anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                >
                    {(() => {
                        const { section, match } = activeGrammar;
                        const m =
                            section != null && match != null && grammarCheckResults[section]
                                ? grammarCheckResults[section][match]
                                : null;
                        return m && m.replacements
                            ? m.replacements.slice(0, 5).map((r, i) => (
                                <MenuItem key={i} onClick={() => applyReplacement(r.value)}>
                                    {r.value}
                                </MenuItem>
                            ))
                            : <MenuItem disabled>No suggestions</MenuItem>;
                    })()}
                </Menu>

                {/* Annotation Panel */}
                <AnnotationPanel
                    projectId={projectId}
                    sectionIndex={selectedSectionIndex}
                    open={isAnnotationPanelOpen}
                    onClose={handleAnnotationClose}
                    onAnnotationAdded={handleAnnotationAdded}
                />

                {/* Pagination */}
                <Box sx={{ textAlign: 'center', mt: 4 }}>
                    <Pagination
                        count={Math.ceil(totalSections / sectionsPerPage)}
                        page={currentPage}
                        onChange={(_e, v) => setCurrentPage(v)}
                    />
                </Box>

                {/* Save Translations */}
                <Box sx={{ textAlign: 'center', mt: 4 }}>
                    <Button
                        variant="contained"
                        color="primary"
                        onClick={handleSaveTranslation}
                        disabled={isSaving}
                        fullWidth
                        sx={{ width: 200, mx: 'auto' }}
                    >
                        Save Translations
                    </Button>
                    {isSaving && (
                        <Box sx={{ width: 200, mx: 'auto', mt: 1 }}>
                            <LinearProgress variant="determinate" value={saveProgress} sx={{ height: 6, borderRadius: 3 }} />
                            <Typography variant="body2" align="center" sx={{ mt: 1 }}>
                                {saveProgress.toFixed(0)}%
                            </Typography>
                        </Box>
                    )}
                </Box>
            </Container>
        </>
    );
};

export default TranslateProject;
